/**
 * useAudioRecording Hook
 *
 * Custom hook for managing audio recording state and operations.
 * Provides a reusable interface for audio recording that can be used
 * in CreateRequest, RequestDetail, or any other screen requiring recording functionality.
 *
 * Features:
 * - Recording state management (idle, recording, paused)
 * - Duration tracking with 100ms precision
 * - Permission handling
 * - Completion callbacks for custom handling (e.g., upload, attachment)
 * - Cross-platform support (web, iOS, Android)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Platform, AppState, AppStateStatus } from 'react-native';
import { audioRecordingService } from '../services/audio';
import type { AudioFile } from '../services/audio';

export type RecordingState = 'idle' | 'recording' | 'paused';

export interface UseAudioRecordingReturn {
  /** Current recording state */
  recordingState: RecordingState;
  /** Elapsed recording time in centiseconds (1/100 second) */
  elapsedTime: number;
  /** Recorded audio file (if any) */
  recordedAudioFile: AudioFile | null;
  /** Start a new recording */
  handleStartRecording: () => Promise<void>;
  /** Pause the current recording */
  handlePauseRecording: () => Promise<void>;
  /** Resume a paused recording */
  handleResumeRecording: () => Promise<void>;
  /** Accept recording and optionally trigger completion callback */
  handleAcceptRecording: (onComplete?: (file: AudioFile) => void | Promise<void>) => Promise<void>;
  /** Discard the current recording */
  handleDiscardRecording: () => Promise<void>;
  /** Clear the recorded audio file from state */
  clearRecordedFile: () => void;
  /** Force cancel any active recording without throwing errors (for cleanup on navigation) */
  forceCancelRecording: () => Promise<void>;
}

/**
 * Custom hook for audio recording functionality
 *
 * @returns Object containing recording state and control functions
 *
 * @example
 * // Basic usage
 * const {
 *   recordingState,
 *   elapsedTime,
 *   handleStartRecording,
 *   handleAcceptRecording,
 * } = useAudioRecording();
 *
 * @example
 * // With completion callback
 * const handleAcceptWithUpload = async () => {
 *   await handleAcceptRecording(async (audioFile) => {
 *     await transcriptionService.uploadAudio(audioFile);
 *   });
 * };
 */
export const useAudioRecording = (): UseAudioRecordingReturn => {
  // State
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [elapsedTime, setElapsedTime] = useState<number>(0); // in centiseconds (1/100 second)
  const [recordedAudioFile, setRecordedAudioFile] = useState<AudioFile | null>(null);

  // Timer ref for duration tracking
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Recording timer effect - updates duration from service
  useEffect(() => {
    if (recordingState === 'recording' || recordingState === 'paused') {
      // Start 100ms interval timer to update display from service
      recordingTimerRef.current = setInterval(() => {
        const currentDuration = audioRecordingService.getCurrentDuration();
        // Convert milliseconds to centiseconds for display
        setElapsedTime(Math.floor(currentDuration / 10));
      }, 100);
    } else {
      // Clear timer when idle
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [recordingState]);

  // Sync hook state with service state when app/tab becomes visible
  // This handles the case where recording was cancelled externally (e.g., by visibility handler in App.tsx)
  // Web: uses document.visibilitychange
  // Native: uses AppState change
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  useEffect(() => {
    // Helper function to sync state when app becomes active
    const syncStateWithService = () => {
      const serviceState = audioRecordingService.getRecordingState();
      if (serviceState === 'idle' && recordingState !== 'idle') {
        // Service was cancelled externally, update our state to match
        setRecordingState('idle');
        setElapsedTime(0);
        setRecordedAudioFile(null);
      }
    };

    if (Platform.OS === 'web') {
      // Web: Handle tab visibility changes
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          syncStateWithService();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      // Native: Handle app state changes
      const handleAppStateChange = (nextAppState: AppStateStatus) => {
        // When app returns to active state, sync our state with service
        if (
          appStateRef.current !== 'active' &&
          nextAppState === 'active'
        ) {
          syncStateWithService();
        }
        appStateRef.current = nextAppState;
      };

      const subscription = AppState.addEventListener('change', handleAppStateChange);
      return () => {
        subscription.remove();
      };
    }
  }, [recordingState]);

  /**
   * Start a new recording
   * Requests microphone permission if needed, then starts recording
   */
  const handleStartRecording = useCallback(async () => {
    try {
      // Request permission first — the service handles the UI
      // (shows a Settings alert if blocked), so just return silently
      const hasPermission = await audioRecordingService.requestPermission();
      if (!hasPermission) {
        return;
      }

      // Start recording
      await audioRecordingService.startRecording();
      setRecordingState('recording');
      setElapsedTime(0);
      setRecordedAudioFile(null); // Clear any previous recording
    } catch (error) {
      console.error('Start recording error:', error);
      // Reset state on error
      setRecordingState('idle');
      setElapsedTime(0);
      throw error; // Re-throw so caller can handle
    }
  }, []);

  /**
   * Pause the current recording
   */
  const handlePauseRecording = useCallback(async () => {
    try {
      await audioRecordingService.pauseRecording();
      setRecordingState('paused');
    } catch (error) {
      console.error('Pause recording error:', error);
      throw error;
    }
  }, []);

  /**
   * Resume a paused recording
   */
  const handleResumeRecording = useCallback(async () => {
    try {
      await audioRecordingService.resumeRecording();
      setRecordingState('recording');
    } catch (error) {
      console.error('Resume recording error:', error);
      throw error;
    }
  }, []);

  /**
   * Accept recording and save it
   * Optionally trigger a completion callback for custom handling
   *
   * @param onComplete - Optional callback to execute after recording is saved
   *                     Receives the AudioFile as parameter
   */
  const handleAcceptRecording = useCallback(async (onComplete?: (file: AudioFile) => void | Promise<void>) => {
    try {
      // Stop and save the recording
      const audioFile = await audioRecordingService.stopAndSave();
      // Store the audio file for future use
      setRecordedAudioFile(audioFile);

      // Reset UI state
      setRecordingState('idle');
      setElapsedTime(0);

      // Execute completion callback if provided
      if (onComplete) {
        await onComplete(audioFile);
      }
    } catch (error) {
      console.error('Accept recording error:', error);
      setRecordingState('idle');
      setElapsedTime(0);
      throw error;
    }
  }, []);

  /**
   * Discard the current recording
   * Cleans up resources and resets state
   */
  const handleDiscardRecording = useCallback(async () => {
    try {
      // Discard the recording
      await audioRecordingService.discard();

      // Reset state
      setRecordingState('idle');
      setElapsedTime(0);
      setRecordedAudioFile(null);
    } catch (error) {
      console.error('Discard recording error:', error);
      // Reset state even on error
      setRecordingState('idle');
      setElapsedTime(0);
      throw error;
    }
  }, []);

  /**
   * Clear the recorded audio file from state
   * Useful after uploading or processing the file
   */
  const clearRecordedFile = useCallback(() => {
    setRecordedAudioFile(null);
  }, []);

  /**
   * Force cancel any active recording without throwing errors
   * Used for cleanup when navigating away or when app goes to background
   */
  const forceCancelRecording = useCallback(async () => {
    try {
      await audioRecordingService.forceCancel();
      // Reset hook state to match service state
      setRecordingState('idle');
      setElapsedTime(0);
      setRecordedAudioFile(null);
    } catch {
      // Never throw from forceCancel - just ensure state is reset
      setRecordingState('idle');
      setElapsedTime(0);
    }
  }, []);

  return {
    recordingState,
    elapsedTime,
    recordedAudioFile,
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleAcceptRecording,
    handleDiscardRecording,
    clearRecordedFile,
    forceCancelRecording,
  };
};
